import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormBuilder, FormControl} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ConfirmDialogComponent} from "../confirm-dialog/confirm-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {IddTrainingService} from "../../services/idd-training.service";

export interface FileResult {
  file: File;
  result?: boolean;
  error?: string;
  uuid?: string;
}

export interface FileBase64 {
  filename: string;
  base64: string;
}

@Component({
  selector: 'app-file-drag-and-drop',
  templateUrl: './file-drag-and-drop.component.html',
  styleUrls: ['./file-drag-and-drop.component.scss']
})
export class FileDragAndDropComponent implements OnInit, OnDestroy {

  @Input() maxSize = 7340032;
  @Input() multiple = false;
  @Input() resetFilesOnAdd = true;
  @Input() fileControl?: FormControl;
  @Input() fileResults?: FileResult[];
  @Input() acceptMime = '*/*';
  @Input() generateOutput = false;
  @Input() required = false;
  @Input() label = null;
  @Input() idd = false;
  @Output() fileSelected: EventEmitter<FileBase64> = new EventEmitter<FileBase64>();
  @Output() filesSelected: EventEmitter<FileBase64[]> = new EventEmitter<FileBase64[]>();
  @Output() fileSizeMore: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() uuid: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('fileDropRef', { static: false }) fileDropEl: ElementRef;

  selectedFiles?: File[] = [];
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private snack: MatSnackBar,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private iddTrainingService: IddTrainingService,
  ) { }

  ngOnInit(): void {
    if (!this.fileControl) {
      this.fileControl = this.fb.control('');
    }

    this.fileControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val: any) => {
        if (!val) {
          this.selectedFiles = [];
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onFilesSelect(fileList: FileList): void {
    let matchSizeLimit = true;
    this.fileSizeMore.emit(false);
    Array.from(fileList).forEach((file: File) => {
      if (file.size > this.maxSize) {
        matchSizeLimit = false;
      }
      if (file.size >   (10 * 1024 * 1024)) {
        this.fileSizeMore.emit(true);
      }
    });

    if (this.resetFilesOnAdd) {
      this.selectedFiles = [];
    } else {
      this.fileSizeMore.emit(false);
      let totalFilesSize = 0;
      Array.from(this.selectedFiles).forEach((file: File) => {
        totalFilesSize += file.size;
      });
      Array.from(fileList).forEach((file: File) => {
        if (file.size > this.maxSize || totalFilesSize + file.size > this.maxSize) {
          matchSizeLimit = false;
          return;
        }else{
          totalFilesSize += file.size;
        }
        if (file.size >  (10 * 1024 * 1024)) {
          this.fileSizeMore.emit(true);
        }
      });
    }
    if (!matchSizeLimit) {
      this.snack.open('Wybrany plik jest za duży!', '', {duration: 2000});
      return;
    }
    if (this.multiple) {
      Array.from(fileList).forEach((file: File) => {
        if (!this.isFileDuplicated(file)) {
          this.selectedFiles.push(file);
        } else{
          this.snack.open('Wybrany plik jest już dodany', '', {duration: 2000});
        }
      });
    } else {
      this.selectedFiles.push(Array.from(fileList)[0]);
    }

    this.fileControl.setValue(this.selectedFiles);

    this.fileDropEl.nativeElement.value = '';

    if (this.generateOutput) {
      this.dispatchEvents();
    }
  }

  dispatchEvents(): void {
    const filesBase64: FileBase64[] = [];
    let processedFiles = 0;

    this.selectedFiles.forEach((file: File) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const fileBase64 = /base64,(.+)/.exec(reader.result.toString());
        filesBase64.push({
          filename: file.name,
          base64: fileBase64 && fileBase64.length > 1 ? fileBase64[1] : ''
        });

        processedFiles++;

        if (processedFiles === this.selectedFiles.length) {
          if (this.multiple) {
            this.filesSelected.emit(filesBase64);
          } else {
            this.fileSelected.emit(filesBase64[0]);
          }
        }
      };
    });

    if (this.selectedFiles.length === 0) {
      if (this.multiple) {
        this.filesSelected.emit([]);
      } else {
        this.fileSelected.emit(null);
      }
    }
  }

  openFileSelect(): void {
    this.fileDropEl.nativeElement.click();
  }

  open(uuid): void {
    if (uuid) {
      this.uuid.emit(uuid);
    }
  }

  unselectFile(index: number, uuid): void {
    this.selectedFiles.splice(index, 1);
    this.fileResults.splice(index, 1);
    if (uuid && this.idd) {
      this.iddTrainingService.deleteCertificate(uuid)
        .subscribe(() => {
          this.snack.open('Pomyślnie usunięto załącznik', '', {duration: 2000});
          // this.fileControl.setValue(this.selectedFiles.length > 0 ? this.selectedFiles : null);
          // this.dispatchEvents();
        });
    }
  }

  isFileDuplicated(file: File): boolean{
    return this.selectedFiles.some((f: File) => f.size === file.size && f.name === file.name && f.lastModified === file.lastModified);
  }
}
